<template>
  <transition :name="transition ?`${ns.namespace.value}-zoom-in-center` : ''">
    <span
      :class="classes"
      :style="style"
      @click="handleClick"
    >
      <span :class="ns.e('content')">
        <slot></slot>
      </span>
      <sg-icon v-if="closable" :class="ns.e('close')" @click="handleClose">
        <Close />
      </sg-icon>
    </span>
  </transition>
</template>

<script lang="ts">
import { computed, defineComponent, watch } from 'vue'
import { TinyColor } from '@ctrl/tinycolor'
import SgIcon from '@sgui-plus/components/icon'
import { Close } from '@sgui-plus/icons-vue'

import { useSize, useNamespace } from '@sgui-plus/hooks'
import { tagProps, tagEmits } from './tag'

export default defineComponent({
  name: 'SgTag',

  components: { SgIcon, Close },

  props: tagProps,
  emits: tagEmits,

  setup(props, { emit }) {
    const tagSize = useSize()
    const ns = useNamespace('tag')
    const classes = computed(() => {
      const { type, closable, fill, outline, checkable, checked } = props
      return [
        ns.b(),
        ns.is('closable', closable),
        ns.m(type),
        ns.m(tagSize.value),
        ns.m(fill ? 'dark' : outline ? 'plain' : 'light'),
        ns.is('checkable', checkable),
        ns.is('checked', checkable && checked)
      ]
    })

    // methods
    const handleClose = (event: MouseEvent) => {
      event.stopPropagation()
      emit('onClose', event)
    }

    const handleClick = (event: MouseEvent) => {
      emit('onClick', event)
    }

    // watch
    if (props.checkable) {
      watch(
        () => props.checked,
        (newVal, oldVal) => {
          if (newVal === oldVal) return
          emit('onChange', newVal)
        }
      )
    }

    const style = computed(() => {
      const style: any = {}

      if (props.checkable && !props.checked) {
        return style
      }

      if (props.color) {
        const color = new TinyColor(props.color)
        const backgroundColor = color.tint(90)
        const borderColor = color.tint(70)

        style['--sg-tag-text-color'] = props.color
        style['--sg-tag-hover-color'] = props.color
        style['--sg-tag-bg-color'] = backgroundColor
        style['--sg-tag-border-color'] = borderColor
      }

      if (props.backgroundColor) {
        style['--sg-tag-bg-color'] = props.backgroundColor
      }

      return style
    })

    return {
      ns,
      classes,
      style,
      handleClose,
      handleClick,
    }
  },
})
</script>
